import React from 'react';
import { IWeatherItem, IWeatherItemDetail } from '@/api/resort/types';
import { cn } from '@/util/cn';

export type ElevationKey = 'base' | 'mid' | 'summit';

// Pull the elevation slice off a weatherItem. Each item has optional
// `.base / .mid / .summit` slots — when the requested level is missing
// we fall back to base so the page still renders something.
export const pickLevel = (
  item: IWeatherItem | undefined,
  level: ElevationKey,
): IWeatherItemDetail | undefined => {
  if (!item) return undefined;
  return (item[level] ?? item.base);
};

export interface ElevationOption {
  key: ElevationKey;
  label: string;
  ft?: number;
}

export const ElevationToggle = ({
  options, current, onChange, formatElev,
}: {
  options: ElevationOption[];
  current: ElevationKey;
  onChange: (key: ElevationKey) => void;
  formatElev: (ft: number) => string;
}) => {
  if (options.length <= 1) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      {options.map((e, i) => (
        <React.Fragment key={e.key}>
          {i > 0 && <span aria-hidden className="text-muted-foreground/40">/</span>}
          <button
            type="button"
            onClick={() => onChange(e.key)}
            className={cn(
              'rounded px-1 py-0.5 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              current === e.key
                ? 'font-bold text-secondary'
                : 'font-medium text-primary hover:text-primary/80 hover:underline',
            )}
          >
            {e.label}
            {e.ft !== undefined && (
              <span
                className={cn(
                  'ml-1.5 font-normal',
                  current === e.key ? 'text-secondary/80' : 'text-muted-foreground',
                )}
              >
                {`(${formatElev(e.ft)})`}
              </span>
            )}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
};
